// @ts-strict-ignore
import { useTranslation, Trans } from "react-i18next";

import { Arrange, Typography, Separator } from "@aviary";
import { AggregatedIngredientList } from "@clinic/shared/components/AggregatedIngredientList/AggregatedIngredientList";
import { useProductDailyIntakeQuery } from "@clinic/shared/data/queries/ProductDailyIntakeQuery/ProductDailyIntake.query";
import { l } from "@emerson/locales";

import { GenericCollapsible } from "../GenericCollapsible/GenericCollapsible";

import * as styles from "./Ingredients.styles";

interface Props {
  servingSize: number;
  servingUnit: string;
  servingsPerContainer: string;
  otherIngredients: string;
  variantId: string;
  isDefaultExpanded?: boolean;
}

const Ingredients = ({
  servingSize,
  servingUnit,
  servingsPerContainer,
  otherIngredients,
  variantId,
  isDefaultExpanded = false,
}: Props) => {
  const { t } = useTranslation();

  const { loading, data: dailyIntakeData } = useProductDailyIntakeQuery({
    variables: {
      id: variantId,
      amount: servingSize?.toString(),
    },
  });

  const dailyIntake = dailyIntakeData?.viewer?.variant?.productDailyIntake;

  const hasIngredients = dailyIntake?.length > 0;
  const hasServingSize = !!servingSize && !!servingUnit;
  const hasOtherIngredients = !!otherIngredients;

  if (!loading && !hasIngredients && !hasServingSize && !hasOtherIngredients) {
    return null;
  }

  const renderOtherIngredients = () =>
    otherIngredients && (
      <Arrange isWrapped={false} isVertical isFullWidth>
        <Separator />
        <Typography type="footnote" isMarginless>
          {t(l.productDetails.OtherIngredients, { otherIngredients })}
        </Typography>
      </Arrange>
    );

  const renderServingSize = () =>
    hasServingSize && (
      <Arrange spacing="xlarge" isWrapped={false} css={styles.container}>
        <Typography type="h5" isMarginless css={styles.lightText}>
          <Trans i18nKey={l.productDetails.ServingSize}>
            <strong>Serving size:</strong> {{ servingSize: ` ${servingSize} ${servingUnit}` }}
          </Trans>
        </Typography>

        {!!servingsPerContainer && (
          <Typography type="h5" isMarginless css={styles.lightText}>
            <Trans i18nKey={l.productDetails.ServingsPerContainer}>
              <strong>Servings per container:</strong> {{ servingsPerContainer }}
            </Trans>
          </Typography>
        )}
      </Arrange>
    );

  const renderAggregatedIngredients = () =>
    dailyIntake?.length > 0 && (
      <div css={styles.container}>
        <Arrange justify="spaceBetween" isWrapped={false}>
          <Typography isMarginless>{t(l.productDetails.Ingredient)}</Typography>
          <Typography isMarginless>{t(l.productDetails.Quantity)}</Typography>
        </Arrange>
        <Separator css={styles.separator} />
        <AggregatedIngredientList
          aggregatedDailyIntake={dailyIntake}
          withIndent={false}
          showProductIcon={false}
          isExpandedByDefault
        />
      </div>
    );

  return (
    <GenericCollapsible
      header={t(l.productDetails.Ingredients)}
      loading={loading}
      isDefaultExpanded={isDefaultExpanded}
    >
      {renderServingSize()}
      {renderAggregatedIngredients()}
      {renderOtherIngredients()}
    </GenericCollapsible>
  );
};

export { Ingredients };
